import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { showGlobalLoader } from './globalLoader';
import { isNoLoaderRoute } from './utils/loaderRoutes';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';

// Public Components
import PublicLayout from './components/public/PublicLayout';
import Home from './components/public/Home';
import About from './components/public/About';
import VisionMission from './components/public/VisionMission';
import CEOMessage from './components/public/CEOMessage';
import Board from './components/public/Board';
import Team from './components/public/Team';
import Services from './components/public/Services';
import MedicalClinics from './components/public/MedicalClinics';
import SurgicalClinics from './components/public/SurgicalClinics';
import DiagnosticServices from './components/public/DiagnosticServices';
import EmergencyServices from './components/public/EmergencyServices';
import Radiology from './components/public/Radiology';
import RadiologyServiceDetail from './components/public/RadiologyServiceDetail';
import Departments from './components/public/Departments';
import DepartmentDetails from './components/public/DepartmentDetails';
import Doctors from './components/public/Doctors';
import Contact from './components/public/Contact';
import Careers from './components/public/Careers';
import PostDetails from './components/public/PostDetails';
import Maintenance from './components/public/Maintenance';
import Admission from './components/public/Admission';
import RoomsAndWards from './components/public/RoomsAndWards';
import MedicalICU from './components/public/MedicalICU';
import SurgicalICU from './components/public/SurgicalICU';
import NeonatalICU from './components/public/NeonatalICU';
import CoronaryCareUnit from './components/public/CoronaryCareUnit';
import HighDependencyUnit from './components/public/HighDependencyUnit';
import OperationTheater from './components/public/OperationTheater';
import InterventionalCardiology from './components/public/InterventionalCardiology';
import PathologyServices from './components/public/PathologyServices';
import EndoscopyERCP from './components/public/EndoscopyERCP';
import EchoETT from './components/public/EchoETT';
import Dialysis from './components/public/Dialysis';
import Daycare from './components/public/Daycare';
import InterventionalRadiology from './components/public/InterventionalRadiology';

// Admin Components
import AdminLayout from './components/admin/AdminLayout';
import AdminDashboard from './components/admin/AdminDashboard';
import ManagePosts from './components/admin/ManagePosts';
import ManageBoardMembers from './components/admin/ManageBoardMembers';
import ManageTeamMembers from './components/admin/ManageTeamMembers';
import ManageJobs from './components/admin/ManageJobs';
import ManageJobApplications from './components/admin/ManageJobApplications';
import ManageContactMessages from './components/admin/ManageContactMessages';
import ManageDoctors from './components/admin/ManageDoctors';
import ManageHomeContent from './components/admin/ManageHomeContent';

import ManageDepartments from './components/admin/ManageDepartments';
import ManageHospitalInfo from './components/admin/ManageHospitalInfo';
import ManageServicePages from './components/admin/ManageServicePages';
import AdminSettings from './components/admin/AdminSettings';
import AdminAccessControl from './components/admin/AdminAccessControl';
import NoAccess from './components/admin/NoAccess';

// Context
import { DataProvider } from './context/DataContext';

const theme = createTheme({
  palette: {
    primary: {
      main: '#2563eb',
      light: '#3b82f6',
      dark: '#1d4ed8',
    },
    secondary: {
      main: '#7c3aed',
      light: '#8b5cf6',
      dark: '#6d28d9',
    },
    background: {
      default: '#f8fafc',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
    },
    h2: {
      fontWeight: 600,
    },
    h3: {
      fontWeight: 600,
    },
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
          fontWeight: 500,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
  },
});

/** Hide the public-site global loader on admin and maintenance routes. */
function GlobalLoaderRouteSync() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (isNoLoaderRoute(pathname)) {
      showGlobalLoader(false);
    }
  }, [pathname]);
  return null;
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <DataProvider>
        <Router>
          <GlobalLoaderRouteSync />
          <Routes>
            {/* Maintenance Route - Standalone */}
            <Route path="/maintenance" element={<Maintenance />} />
            
            {/* Public Website Routes */}
            <Route path="/" element={<PublicLayout />}>
              <Route index element={<Home />} />
              <Route path="about" element={<About />} />
              <Route path="about/vision-mission" element={<VisionMission />} />
              <Route path="about/ceo-message" element={<CEOMessage />} />
              <Route path="about/board" element={<Board />} />
              <Route path="about/team" element={<Team />} />
              <Route path="services" element={<Services />} />
              <Route path="departments" element={<Departments />} />
              <Route path="services/medical-clinics" element={<MedicalClinics />} />
              <Route path="services/surgical-clinics" element={<SurgicalClinics />} />
              <Route path="services/diagnostic-services" element={<DiagnosticServices />} />
              <Route path="services/emergency-services" element={<EmergencyServices />} />
              <Route path="services/radiology" element={<Radiology />} />
              <Route path="services/radiology/:serviceSlug" element={<RadiologyServiceDetail />} />
              <Route path="services/interventional-radiology" element={<InterventionalRadiology />} />
              <Route path="services/pathology-services" element={<PathologyServices />} />
              <Route path="services/admission" element={<Admission />} />
              <Route path="services/rooms-wards" element={<RoomsAndWards />} />
              <Route path="services/medical-icu" element={<MedicalICU />} />
              <Route path="services/surgical-icu" element={<SurgicalICU />} />
              <Route path="services/neonatal-icu" element={<NeonatalICU />} />
              <Route path="services/coronary-care-unit" element={<CoronaryCareUnit />} />
              <Route path="services/high-dependency-unit" element={<HighDependencyUnit />} />
              <Route path="services/operation-theater" element={<OperationTheater />} />
              <Route path="services/interventional-cardiology" element={<InterventionalCardiology />} />
              <Route path="services/endoscopy-ercp" element={<EndoscopyERCP />} />
              <Route path="services/echo-ett" element={<EchoETT />} />
              <Route path="services/dialysis" element={<Dialysis />} />
              <Route path="services/daycare" element={<Daycare />} />
              <Route path="department/:departmentId" element={<DepartmentDetails />} />
              <Route path="posts/:id" element={<PostDetails />} />
              <Route path="doctors" element={<Doctors />} />
              <Route path="contact" element={<Contact />} />
              <Route path="careers" element={<Careers />} />
            </Route>

            {/* Admin Dashboard Routes */}
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<AdminDashboard />} />
              <Route path="posts" element={<ManagePosts />} />
              <Route path="board-members" element={<ManageBoardMembers />} />
              <Route path="team-members" element={<ManageTeamMembers />} />
              <Route path="jobs" element={<ManageJobs />} />
              <Route path="job-applications" element={<ManageJobApplications />} />
              <Route path="contact-messages" element={<ManageContactMessages />} />
              <Route path="doctors" element={<ManageDoctors />} />
              <Route path="home-content" element={<ManageHomeContent />} />

              <Route path="departments" element={<ManageDepartments />} />
              <Route path="hospital-info" element={<ManageHospitalInfo />} />
              <Route path="services-pages" element={<ManageServicePages />} />
              <Route path="settings" element={<AdminSettings />} />
              <Route path="access-control" element={<AdminAccessControl />} />
              <Route path="no-access" element={<NoAccess />} />
            </Route>
          </Routes>
        </Router>
      </DataProvider>
    </ThemeProvider>
  );
}

export default App; 